<template>
  <div>
    <v-row dense>
      <v-col cols="12" md="6">
        <BTextarea
          v-model="form.name"
          validate="required"
          label="Название"
          :disabled="disabled"
          name="name"
          :rows="1"
          autofocus
        />
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12" md="auto">
        <BTextFieldDate
          v-model="form.start_at"
          validate="required"
          :min="publication.start_at"
          :max="publication.end_at || form.end_at"
          clearable
          label="Дата начала"
          placeholder="Введите дату"
          :disabled="disabled"
          name="start_at"
          datetime
        />
      </v-col>

      <v-col cols="12" md="auto">
        <BTextFieldDate
          v-model="form.end_at"
          validate="required"
          :min="publication.start_at || form.start_at"
          :max="publication.end_at"
          clearable
          label="Дата окончания"
          placeholder="Введите дату"
          :disabled="disabled"
          name="end_at"
          datetime
        />
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12" md="6" lg="4">
        <BAutocompleteCommon
          v-model="form.form_template"
          validate="required"
          $type="form-template"
          :disabled="disabled"
          name="form_template_id"
        />
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12">
        <v-checkbox
          v-model="form.collect_intermediate_revisions"
          :disabled="disabled"
          label="Собирать промежуточный результат"
          dense
          hide-details
          class="ma-0 pa-0 mr-2"
          name="collect_intermediate_revisions"
        />
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12">
        <v-checkbox
          v-model="form.use_excel"
          :disabled="disabled"
          label="Заполнение из Excel"
          dense
          hide-details
          class="ma-0 pa-0 mr-2"
          name="use_excel"
        />
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12">
        <v-checkbox
          v-model="form.is_report_view"
          :disabled="disabled"
          label="Отображать в групповом отчете"
          dense
          hide-details
          class="ma-0 pa-0 mr-2"
          name="is_report_view"
        />
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12">
        <v-checkbox
          v-model="form.is_scannable"
          :disabled="disabled"
          label="Отображать раздел &quot;Сканирование&quot;"
          dense
          hide-details
          class="ma-0 pa-0 mr-2"
          name="is_scannable"
        />
      </v-col>
    </v-row>

    <v-divider class="mt-4" />

    <template v-if="!!form.id">
      <v-row dense class="my-2">
        <v-col>
          <BTableWithAutocompleteAsync
            :value="participants"
            :actions="actions"
            :readonly="disabled"
            :loading="isLoading"
            type-table="participants"
            type-autocomplete="participants"
            name="participants"
            return-object
            @action:append="onAppend"
            @action:delete="onRemove"
            @action:edit="onEditParticipant"
          >
            <template #header>
              <h3 class="mb-2">
                Участники
              </h3>
            </template>
          </BTableWithAutocompleteAsync>
        </v-col>
      </v-row>

      <v-divider class="mt-2" />
    </template>
  </div>
</template>

<script>
import BTextarea from '@/components/forms/BTextarea.vue'
import BTextFieldDate from '@/components/forms/BTextFieldDate.vue'
import BAutocompleteCommon from '@/components/forms/BAutocompleteCommon.vue'
import BTableWithAutocompleteAsync from '@/components/common/BTable/BTableWithAutocompleteAsync.vue'
import { createPublicationsParticipantModalModal } from '@/views/Publications/utils'

import { unionBy } from 'lodash'
import { DataCollectionsApi } from '@/api/resources'
import { Adapter } from '@/store/createCrudModule'
import { DELETE, EDIT } from '@/helpers/enums/table-actions'

export const TABLE_ACTIONS = Object.freeze([
  EDIT,
  DELETE,
])


export const createDefaultData = (start_at, end_at) => ({
  // id
  name: '',

  publication: null,
  form_template: null,
  start_at,
  end_at,
  participants: [],
  collect_intermediate_revisions: false,
  use_excel: true,
  is_report_view: false,
  is_scannable: false,
  // created_at
  // updated_at
})

export default {
  name: 'PublicationDataCollectionsForm',
  components: {
    BTextarea,
    BTextFieldDate,
    BAutocompleteCommon,
    BTableWithAutocompleteAsync,
  },
  props: {
    disabled: Boolean,
    form: {
      type: Object,
      required: true,
    },
    publication: {
      type: Object,
      required: true,
      validator: (prop) => (
        prop
        && 'start_at' in prop
        && 'end_at' in prop
      ),
    },
  },
  data: () => ({
    isLoading: false,
    actions: TABLE_ACTIONS,
  }),
  computed: {
    participants() {
      if (this.form.participants && this.form.participants.length > 0) return this.form?.participants
      return []
    },
  },
  created() {
    const api = new DataCollectionsApi()
    this.adapter = new Adapter(api)

    this.onGetData()
  },
  methods: {
    async onGetData() {
      if (!this.form.id) {
        return
      }

      this.isLoading = true
      try {
        const { data } = await this.adapter.api.getOne(this.form.id)


        this.$set(this.form, 'participants', data.participants || [])
      } catch (e) {
        this.$notice.errors(e)
      }
      this.isLoading = false
    },
    async onAppend(data) {
      this.isLoading = true
      try {
        await this.adapter.api.addParticipants(
          this.form.id,
          { publication_id: this.publication.id, participants_id: data.id },
        )

        this.form.participants = unionBy(this.form.participants, [data], 'id')
      } catch (e) {
        this.$notice.errors(e)
      }
      this.isLoading = false
    },
    async onRemove(data, callback) {
      const unwatch = callback?.()
      this.isLoading = true
      try {
        await this.adapter.api.removeParticipants(
          this.form.id,
          { publication_id: this.publication.id, participants_id: data.id },
        )

        const value = this.form.participants.slice()
        const index = value.findIndex(({ id }) => id === data.id)
        if (index >= 0) {
          value.splice(index, 1)
          this.form.participants = value
        }
      } catch (e) {
        this.$notice.errors(e)
      } finally {
        this.isLoading = false
        unwatch?.()
      }
    },
    async getParticipants() {
      this.isLoading = true
      try {
        const { data } = await this.adapter.api.getOne(
          this.form.id,
          { publication_id: this.publication.id },
        )
        this.form.participants = [].concat(data.participants)
      } catch (e) {
        this.$notice.errors(e)
      }
      this.isLoading = false
    },
    async onEditParticipant(participant) {
      const propsData = { participant_id: participant.id }
      const modal = await createPublicationsParticipantModalModal(this, propsData)
      modal.$on('closed', this.getParticipants)
      await modal.open()
    },
  },
}
</script>
